"use strict";
/**
 * Thread UI actions bound onto AudRoam $scope.
 *
 * Server (Python /api/v2/threads) is source of truth.
 * localStorage is write-through cache only (pills + digest-stable lists).
 * Pull: threadServerSync.pullThreadsToCache (modular, tested).
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.registerThreadScopeActions = exports.resolveMemberLabel = exports.captionFromPilotRow = exports.isGenericMemberLabel = exports.recentNotesForThreadAdd = void 0;
var threadServerSync_1 = require("./threadServerSync");
var threadMemberDigest_1 = require("./threadMemberDigest");
var threadStore_1 = require("./threadStore");
function personIdOf(host) {
    var _a, _b, _c;
    return (_b = (_a = host.person) === null || _a === void 0 ? void 0 : _a.dbPK) !== null && _b !== void 0 ? _b : (_c = host.person) === null || _c === void 0 ? void 0 : _c.personId;
}
function storageOf(deps) {
    return deps.storage || (typeof localStorage !== 'undefined' ? localStorage : {
        getItem: function () { return null; },
        setItem: function () { return undefined; },
        removeItem: function () { return undefined; },
    });
}
function emptyThreadUi() {
    return {
        showPanel: false,
        mode: 'list',
        activeThreadId: null,
        activeThread: null,
        members: [],
        memberLabels: {},
        createKind: 'invoice',
        createTitle: '',
        createAccountId: '',
        pendingRow: null,
        message: null,
        error: null,
        recentChoices: [],
        addMemberId: '',
        recentNoteChoices: [],
    };
}
/**
 * View-history notes only (excludes thread panel visits).
 * Used in the open-thread panel to add members quickly.
 */
function recentNotesForThreadAdd(viewHistoryEntries, alreadyMemberIds, limit) {
    if (limit === void 0) { limit = 12; }
    var memberSet = alreadyMemberIds instanceof Set
        ? alreadyMemberIds
        : new Set((alreadyMemberIds || []).map(function (x) { return String(x); }));
    var seen = new Set();
    var out = [];
    for (var _i = 0, _a = viewHistoryEntries || []; _i < _a.length; _i++) {
        var entry = _a[_i];
        var data = (entry === null || entry === void 0 ? void 0 : entry.data) || {};
        if (data.threadId || (0, threadStore_1.isThreadViewUrl)(data.url))
            continue;
        if (data.pnid == null || data.pnid === '')
            continue;
        var pnid = String(data.pnid);
        if (seen.has(pnid))
            continue;
        seen.add(pnid);
        out.push({
            pnid: pnid,
            caption: (entry.caption || data.title || "Note #".concat(pnid)).trim() || "Note #".concat(pnid),
            alreadyMember: memberSet.has(pnid),
            viewedAt: Number(entry.timestamp) || 0,
        });
        if (out.length >= limit)
            break;
    }
    return out;
}
exports.recentNotesForThreadAdd = recentNotesForThreadAdd;
function threadViewMeta(thread) {
    return {
        title: thread.title,
        taskGroup: 'Thread',
        topicGroup: thread.kind,
        bodyPreview: thread.title,
        account: thread.accountId || undefined,
        threadId: thread.id,
        threadKind: thread.kind,
    };
}
function noteIdFallback(memberId) {
    return "Note #".concat(memberId);
}
/** True when label is empty or the generic "Note #id" / bare id (not a real caption). */
function isGenericMemberLabel(label, memberId) {
    if (label == null)
        return true;
    var t = String(label).trim();
    if (!t)
        return true;
    var mid = String(memberId);
    return t === noteIdFallback(mid) || t === mid || t === "#".concat(mid);
}
exports.isGenericMemberLabel = isGenericMemberLabel;
function firstBodyLine(body) {
    return (String(body || '')
        .split('\n')
        .map(function (l) { return l.trim(); })
        .find(function (l) { return l.length > 0; }) || '');
}
/** Caption from a loaded pilot_note row (task/topic + first body line). */
function captionFromPilotRow(row, memberId) {
    var _a;
    var mid = String(memberId != null ? memberId : (_a = row === null || row === void 0 ? void 0 : row.ID) !== null && _a !== void 0 ? _a : '');
    if (!row)
        return noteIdFallback(mid || '?');
    var task = row.pnTaskGroup || '';
    var topic = row.pnTopicGroup || '';
    var body = firstBodyLine(row.pnBody);
    var parts = [task, topic].filter(Boolean);
    var head = parts.join(' · ');
    var preview = body ? body.slice(0, 48) : '';
    if (head && preview)
        return "".concat(head, " \u2014 ").concat(preview);
    if (preview)
        return preview;
    if (head)
        return head;
    return noteIdFallback(mid);
}
exports.captionFromPilotRow = captionFromPilotRow;
function findRowLabel(host, memberId) {
    var lists = [host.reportResult, host.recentRows, host.teamRecent, host.scheduleRows].filter(Boolean);
    for (var _i = 0, lists_1 = lists; _i < lists_1.length; _i++) {
        var list = lists_1[_i];
        for (var _a = 0, list_1 = list; _a < list_1.length; _a++) {
            var row = list_1[_a];
            if (row && String(row.ID) === memberId) {
                return captionFromPilotRow(row, memberId);
            }
        }
    }
    return noteIdFallback(memberId);
}
function findHistoryCaption(host, memberId) {
    var _a;
    var views = ((_a = host.history) === null || _a === void 0 ? void 0 : _a.views) || [];
    for (var _i = 0, views_1 = views; _i < views_1.length; _i++) {
        var entry = views_1[_i];
        var data = (entry === null || entry === void 0 ? void 0 : entry.data) || {};
        if (data.threadId || (0, threadStore_1.isThreadViewUrl)(data.url))
            continue;
        if (data.pnid == null)
            continue;
        if (String(data.pnid) !== memberId)
            continue;
        var cap = (entry.caption || data.title || '').trim();
        if (cap && !isGenericMemberLabel(cap, memberId))
            return cap;
    }
    return null;
}
/**
 * Display label for a thread member.
 * Prefer live row lists, then stored pathHint, then view-history caption, then Note #id.
 * pathHint is critical after remove/reopen when the note is no longer in reportResult/recent.
 */
function resolveMemberLabel(host, member) {
    var mid = String(member.memberId);
    var live = findRowLabel(host, mid);
    if (!isGenericMemberLabel(live, mid))
        return live;
    var hint = (member.pathHint || '').trim();
    if (hint && !isGenericMemberLabel(hint, mid))
        return hint;
    var hist = findHistoryCaption(host, mid);
    if (hist)
        return hist;
    return noteIdFallback(mid);
}
exports.resolveMemberLabel = resolveMemberLabel;
/** Best-effort pathHint when adding a member (persist so later opens keep caption). */
function pathHintForMember(host, memberId) {
    var live = findRowLabel(host, memberId);
    if (!isGenericMemberLabel(live, memberId))
        return live;
    var hist = findHistoryCaption(host, memberId);
    if (hist)
        return hist;
    return live;
}
/** Prefer caption from the row being added (menu/source row) over list/history lookup. */
function pathHintForRow(host, row) {
    var _a;
    if (row && row.ID != null) {
        var fromRow = captionFromPilotRow(row, row.ID);
        if (!isGenericMemberLabel(fromRow, row.ID))
            return fromRow;
    }
    return pathHintForMember(host, String((_a = row === null || row === void 0 ? void 0 : row.ID) !== null && _a !== void 0 ? _a : ''));
}
function registerThreadScopeActions(host, deps) {
    var historyService = deps.historyService;
    var storage = storageOf(deps);
    var threadsApi = deps.threadsApi || null;
    var $q = deps.$q || null;
    var $timeout = deps.$timeout;
    host.threadKinds = threadStore_1.THREAD_KINDS.slice();
    host.threadUi = emptyThreadUi();
    host.threadsServerEnabled = !!threadsApi;
    host.threadsSyncState = { loading: false, lastError: null, lastAt: null };
    host.threadStatusLabel = threadStore_1.threadStatusLabel;
    host.threadStatusPillClass = threadStore_1.threadStatusPillClass;
    /**
     * Template ng-repeat watches (e.g. recentThreadsForAdd(row), listAllThreads()) must
     * return a stable array reference across digests. Fresh objects every call → $rootScope:infdig.
     */
    var threadListGen = 0;
    var allThreadsCache = null;
    var recentAddCache = new Map();
    var pullGate = (0, threadServerSync_1.createThreadPullGate)();
    function deviceIdForDigest() {
        try {
            if (typeof localStorage === 'undefined')
                return undefined;
            var id = localStorage.getItem('audroam.device_id');
            if (!id) {
                id = Date.now().toString(36) + Math.random().toString(36).slice(2);
                localStorage.setItem('audroam.device_id', id);
            }
            return id;
        }
        catch (_a) {
            return undefined;
        }
    }
    /**
     * Fan-out membership progress on history focus SSE (same bus as recentDigest).
     * Peers apply via applyThreadMemberDigestFromFocus → local cache + pills.
     */
    function publishThreadMemberPatches(patches) {
        var _a, _b;
        var dig = (0, threadMemberDigest_1.buildThreadMemberDigest)(patches, {
            at: Date.now(),
            deviceId: deviceIdForDigest(),
        });
        if (!dig)
            return;
        var pid = personIdOf(host);
        for (var _i = 0, _c = dig.patches; _i < _c.length; _i++) {
            var p = _c[_i];
            (0, threadMemberDigest_1.touchThreadMemberPatchAt)(pid, p.threadId, p.memberId, p.at, storage, p.memberType || 'pilot_note');
        }
        var account = (_a = host.person) === null || _a === void 0 ? void 0 : _a.CurrentAccount;
        var realm = (_b = host.person) === null || _b === void 0 ? void 0 : _b.CurrentRealm;
        if (!account || !realm)
            return;
        try {
            historyService.addFocusEntry(String(realm), String(account), {
                threadMemberDigest: dig,
                force: true,
            });
        }
        catch (_d) {
            /* stream optional */
        }
    }
    /** Apply peer threadMemberDigest from history:remoteFocus (or bulk merge). */
    host.applyThreadMemberDigestFromFocus = function (entryOrData) {
        var _a;
        var data = entryOrData && entryOrData.data && typeof entryOrData.data === 'object'
            ? entryOrData.data
            : entryOrData;
        var dig = (0, threadMemberDigest_1.extractThreadMemberDigestFromFocusData)(data);
        if (!dig)
            return false;
        var pid = personIdOf(host);
        var result = (0, threadMemberDigest_1.applyThreadMemberDigestToCache)(pid, dig, storage);
        if (result.applied > 0) {
            invalidateThreadListCache();
            host.refreshThreadPills();
            if (((_a = host.threadUi) === null || _a === void 0 ? void 0 : _a.showPanel) &&
                host.threadUi.activeThreadId &&
                dig.patches.some(function (p) { return p.threadId === host.threadUi.activeThreadId; })) {
                try {
                    openThreadFromCache(host.threadUi.activeThreadId, { skipHistory: true });
                }
                catch (_b) {
                    /* panel optional */
                }
            }
            if ($timeout) {
                $timeout(function () { return undefined; }, 0);
            }
        }
        if (result.needsThreadPull && threadsApi) {
            host.refreshThreadsFromServer({ quiet: true });
        }
        return result.applied > 0;
    };
    // Same-origin other tabs: localStorage write-through → re-paint pills
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        var onStorage = function (ev) {
            if (!(0, threadMemberDigest_1.isThreadCacheStorageKey)(ev.key))
                return;
            try {
                invalidateThreadListCache();
                host.refreshThreadPills();
                if ($timeout)
                    $timeout(function () { return undefined; }, 0);
            }
            catch (_a) {
                /* ignore */
            }
        };
        window.addEventListener('storage', onStorage);
        // No easy $destroy here; listener is cheap and host lives for the SPA session
    }
    function viewsFingerprint(views) {
        if (!views || !views.length)
            return '0';
        var first = views[0];
        var last = views[views.length - 1];
        return "".concat(views.length, ":").concat((first === null || first === void 0 ? void 0 : first.timestamp) || 0, ":").concat((last === null || last === void 0 ? void 0 : last.timestamp) || 0);
    }
    function invalidateThreadListCache() {
        threadListGen += 1;
        allThreadsCache = null;
        recentAddCache.clear();
    }
    function resolvedPromise(value) {
        if ($q)
            return $q.when(value);
        return Promise.resolve(value);
    }
    host.invalidateThreadListCache = invalidateThreadListCache;
    host.personIdForThreads = function () {
        return personIdOf(host);
    };
    host.refreshThreadPills = function () {
        var pid = personIdOf(host);
        (0, threadStore_1.attachThreadPillsToRows)(pid, host.reportResult, storage);
        (0, threadStore_1.attachThreadPillsToRows)(pid, host.recentRows, storage);
        (0, threadStore_1.attachThreadPillsToRows)(pid, host.teamRecent, storage);
        (0, threadStore_1.attachThreadPillsToRows)(pid, host.scheduleRows, storage);
    };
    /**
     * Pull server → replace local cache. Alias: syncThreadsFromServer (AudRoamCtrl).
     * Applies full table fields (status, role, pathHint, …) via normalize on write-through.
     */
    host.refreshThreadsFromServer = function (opts) {
        if (!threadsApi) {
            return resolvedPromise(undefined);
        }
        var pid = personIdOf(host);
        if (pid == null) {
            return resolvedPromise(undefined);
        }
        var quiet = !!(opts === null || opts === void 0 ? void 0 : opts.quiet);
        host.threadsSyncState = __assign(__assign({}, host.threadsSyncState), { loading: true, lastError: null });
        var applyOk = function (result) {
            invalidateThreadListCache();
            host.refreshThreadPills();
            host.threadsSyncState = {
                loading: false,
                lastError: null,
                lastAt: Date.now(),
            };
            if (!quiet && typeof host.toast === 'function' && result.threads.length) {
                host.toast("Threads loaded (".concat(result.threads.length, ")"), 'info', 2000);
            }
            // Ensure Angular digests when native Promise path resolves outside $http
            if (deps.$timeout) {
                deps.$timeout(function () { return undefined; }, 0);
            }
        };
        var applyErr = function (err) {
            var msg = (err === null || err === void 0 ? void 0 : err.message) || 'Thread load failed';
            host.threadsSyncState = {
                loading: false,
                lastError: msg,
                lastAt: Date.now(),
            };
            if (!quiet && typeof host.toast === 'function') {
                host.toast("Threads: ".concat(msg), 'warning', 4000);
            }
            if (deps.$timeout) {
                deps.$timeout(function () { return undefined; }, 0);
            }
        };
        var run = pullGate(pid, { threadsApi: threadsApi, storage: storage }).then(applyOk, applyErr);
        return ($q ? $q.when(run) : run);
    };
    /** Patch thread header (status / title / kind) and write-through cache. */
    host.setThreadStatus = function (threadId, status) {
        if (!threadId || status == null || String(status).trim() === '')
            return;
        var pid = personIdOf(host);
        var nextStatus = String(status).trim();
        var finish = function (remote) {
            var _a;
            (0, threadStore_1.upsertLocalThread)(pid, remote, storage);
            invalidateThreadListCache();
            host.refreshThreadPills();
            if (((_a = host.threadUi) === null || _a === void 0 ? void 0 : _a.showPanel) && host.threadUi.activeThreadId === threadId) {
                openThreadFromCache(threadId, { skipHistory: true });
            }
        };
        if (!threadsApi) {
            var next = (0, threadStore_1.updateThread)(pid, threadId, { status: nextStatus }, storage);
            if (next)
                finish(next);
            return;
        }
        threadsApi
            .updateThread(threadId, { status: nextStatus })
            .then(function (remote) { return finish(remote); })
            .catch(function (e) {
            try {
                var next = (0, threadStore_1.updateThread)(pid, threadId, { status: nextStatus }, storage);
                if (next) {
                    finish(next);
                    return;
                }
            }
            catch (_a) {
                /* fall through */
            }
            if (typeof host.toast === 'function') {
                host.toast((e === null || e === void 0 ? void 0 : e.message) || 'Could not update thread status', 'warning', 4000);
            }
        });
    };
    // Back-compat name (login / panel open)
    host.syncThreadsFromServer = function (opts) {
        return host.refreshThreadsFromServer({ quiet: opts === null || opts === void 0 ? void 0 : opts.quiet });
    };
    host.listAllThreads = function () {
        var pid = personIdOf(host);
        if (allThreadsCache &&
            allThreadsCache.gen === threadListGen &&
            allThreadsCache.personId === pid) {
            return allThreadsCache.threads;
        }
        var threads = (0, threadStore_1.listThreads)(pid, storage);
        allThreadsCache = { gen: threadListGen, personId: pid, threads: threads };
        return threads;
    };
    host.recentThreadsForAdd = function (row) {
        var _a;
        var pid = personIdOf(host);
        var rowKey = (row === null || row === void 0 ? void 0 : row.ID) != null && row.ID !== '' ? String(row.ID) : '';
        var views = historyService.getViewHistory
            ? historyService.getViewHistory()
            : ((_a = host.history) === null || _a === void 0 ? void 0 : _a.views) || [];
        var viewsSig = viewsFingerprint(views);
        var cacheKey = "".concat(pid !== null && pid !== void 0 ? pid : '', "::").concat(rowKey);
        var hit = recentAddCache.get(cacheKey);
        if (hit && hit.gen === threadListGen && hit.viewsSig === viewsSig) {
            return hit.choices;
        }
        var choices = (0, threadStore_1.recentThreadsForAdd)(pid, row === null || row === void 0 ? void 0 : row.ID, views, storage, 8);
        recentAddCache.set(cacheKey, { gen: threadListGen, viewsSig: viewsSig, choices: choices });
        return choices;
    };
    /** Threads this row is already on (for unlink / check cycle in ⋮ menu). */
    host.threadsOnRow = function (row) {
        if (!row || row.ID == null)
            return [];
        var pid = personIdOf(host);
        var memberships = (0, threadStore_1.listMembershipsForRow)(pid, row.ID, storage);
        if (!memberships.length)
            return [];
        var byId = new Map((0, threadStore_1.listThreads)(pid, storage).map(function (t) { return [t.id, t]; }));
        var out = [];
        for (var _i = 0, memberships_1 = memberships; _i < memberships_1.length; _i++) {
            var m = memberships_1[_i];
            var t = byId.get(m.threadId);
            if (!t)
                continue;
            var memberStatus = (0, threadStore_1.memberEffectiveStatus)(m);
            var mark = (0, threadStore_1.memberCheckMark)(memberStatus);
            var progressTitle = (0, threadStore_1.memberCheckTitle)(memberStatus);
            var thrStatus = t.status || 'open';
            out.push({
                threadId: t.id,
                kind: t.kind,
                title: t.title,
                label: (0, threadStore_1.threadPillLabel)(t),
                status: thrStatus,
                statusLabel: (0, threadStore_1.threadStatusLabel)(thrStatus),
                statusClass: (0, threadStore_1.threadStatusPillClass)(thrStatus),
                role: m.role,
                memberStatus: memberStatus,
                statusMark: mark,
                statusTitle: progressTitle,
                checkMark: mark,
                checkTitle: progressTitle,
            });
        }
        return out;
    };
    host.memberCheckMark = threadStore_1.memberCheckMark;
    host.memberCheckTitle = threadStore_1.memberCheckTitle;
    /** Stable list for pill / panel progress dropdown (ng-repeat safe). */
    host.memberProgressOptions = threadStore_1.MEMBER_PROGRESS_OPTIONS;
    /** Accordion sections for pill status menu. */
    host.memberProgressSections = threadStore_1.MEMBER_PROGRESS_SECTIONS;
    host.memberProgressSectionIdForStatus = threadStore_1.memberProgressSectionIdForStatus;
    host.toggleThreadPillMenu = function (row, threadId, $event) {
        if ($event) {
            $event.preventDefault();
            $event.stopPropagation();
        }
        if (!row)
            return;
        var openId = row._threadPillMenuId === threadId ? null : threadId;
        row._threadPillMenuId = openId;
    };
    host.closeThreadPillMenu = function (row) {
        if (row)
            row._threadPillMenuId = null;
    };
    host.recordThreadView = function (thread) {
        if (!(thread === null || thread === void 0 ? void 0 : thread.id))
            return;
        var url = (0, threadStore_1.threadViewUrl)(thread.id);
        historyService.addViewEntry(undefined, url, threadViewMeta(thread));
        if (host.history && historyService.getAllHistory) {
            host.history = historyService.getAllHistory();
        }
    };
    host.refreshRecentNoteChoices = function (threadId) {
        var _a, _b;
        var tid = threadId || ((_a = host.threadUi) === null || _a === void 0 ? void 0 : _a.activeThreadId);
        var pid = personIdOf(host);
        var memberIds = tid
            ? (0, threadStore_1.listMembers)(pid, tid, storage).map(function (m) { return m.memberId; })
            : [];
        var views = historyService.getViewHistory
            ? historyService.getViewHistory()
            : ((_b = host.history) === null || _b === void 0 ? void 0 : _b.views) || [];
        var choices = recentNotesForThreadAdd(views, memberIds, 12);
        if (host.threadUi) {
            host.threadUi.recentNoteChoices = choices;
        }
        return choices;
    };
    function syncThreadLocation(url, opts) {
        if (typeof window === 'undefined' || !window.history)
            return;
        try {
            var current = (window.location.pathname || '') + (window.location.search || '');
            var next = url || '/';
            if (current === next || current === next + '/')
                return;
            if ((opts === null || opts === void 0 ? void 0 : opts.replace) && typeof window.history.replaceState === 'function') {
                window.history.replaceState({ audroamThreads: true }, '', next);
            }
            else if (typeof window.history.pushState === 'function') {
                window.history.pushState({ audroamThreads: true }, '', next);
            }
        }
        catch (_a) {
            /* ignore */
        }
    }
    function scrollThreadPanelIntoView() {
        var run = function () {
            try {
                var el = document.querySelector('.thread-panel');
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            }
            catch (_a) {
                /* ignore */
            }
        };
        if (deps.$timeout) {
            deps.$timeout(run, 50);
        }
        else {
            run();
        }
    }
    function applyThreadsPath(pathname) {
        var parsed = (0, threadStore_1.parseThreadsPath)(pathname || (typeof window !== 'undefined' ? window.location.pathname : ''));
        if (parsed.threadId) {
            host.openThread(parsed.threadId, { skipHistory: true });
            return;
        }
        if (parsed.list) {
            host.showThreadList();
        }
    }
    function openThreadFromCache(threadId, opts) {
        var _a, _b;
        var pid = personIdOf(host);
        var thread = (0, threadStore_1.getThread)(pid, threadId, storage);
        if (!thread) {
            return false;
        }
        var members = (0, threadStore_1.listMembers)(pid, threadId, storage);
        // Keep host.history.views in sync so pathHint/history fallbacks work after remove/reopen.
        var views = historyService.getViewHistory
            ? historyService.getViewHistory()
            : ((_a = host.history) === null || _a === void 0 ? void 0 : _a.views) || [];
        if (host.history) {
            host.history.views = views;
        }
        else {
            host.history = { views: views };
        }
        var labels = {};
        var membersWithHints = members.map(function (m) {
            var label = resolveMemberLabel(host, m);
            labels[m.memberId] = label;
            // Backfill local pathHint when we resolved a real caption but cache only has Note #id
            // (e.g. older adds, or server never stored path_hint). Survives next open/remove refresh.
            if (!isGenericMemberLabel(label, m.memberId) &&
                isGenericMemberLabel(m.pathHint, m.memberId)) {
                var next = __assign(__assign({}, m), { pathHint: label });
                (0, threadStore_1.upsertLocalMember)(pid, next, storage);
                return next;
            }
            return m;
        });
        var recentNoteChoices = recentNotesForThreadAdd(views, membersWithHints.map(function (m) { return m.memberId; }), 12);
        host.threadUi = __assign(__assign({}, emptyThreadUi()), { showPanel: true, mode: 'detail', activeThreadId: thread.id, activeThread: thread, members: membersWithHints, memberLabels: labels, createAccountId: thread.accountId || ((_b = host.person) === null || _b === void 0 ? void 0 : _b.CurrentAccount) || '', recentNoteChoices: recentNoteChoices, addMemberId: '' });
        if (!(opts === null || opts === void 0 ? void 0 : opts.skipHistory)) {
            host.recordThreadView(thread);
        }
        var caption = (0, threadStore_1.threadCaption)(thread);
        if (host.threadUi.activeThread) {
            host.threadUi.caption = caption;
        }
        syncThreadLocation((0, threadStore_1.threadViewUrl)(thread.id));
        scrollThreadPanelIntoView();
        return true;
    }
    host.openThread = function (threadId, opts) {
        if (!threadId)
            return;
        var pid = personIdOf(host);
        // Prefer server detail (includes members), write-through cache, then paint.
        if (threadsApi) {
            threadsApi
                .getThread(threadId)
                .then(function (remote) {
                (0, threadStore_1.upsertLocalThread)(pid, remote, storage);
                if (Array.isArray(remote.members)) {
                    (0, threadStore_1.replaceLocalMembersForThread)(pid, threadId, remote.members, storage);
                }
                invalidateThreadListCache();
                if (!openThreadFromCache(threadId, opts)) {
                    if (typeof host.toast === 'function') {
                        host.toast('Thread not found', 'warning', 4000);
                    }
                }
                else {
                    host.refreshThreadPills();
                }
            })
                .catch(function () {
                if (!openThreadFromCache(threadId, opts)) {
                    if (typeof host.toast === 'function') {
                        host.toast('Thread not found (not on server or local cache).', 'warning', 4000);
                    }
                }
            });
            return;
        }
        if (!openThreadFromCache(threadId, opts)) {
            if (typeof host.toast === 'function') {
                host.toast('Thread not found (local only for this person).', 'warning', 4000);
            }
        }
    };
    /** Add pilot_note by numeric/string id (detail panel input or recent list). */
    host.addMemberById = function (memberId, threadId) {
        var ui = host.threadUi || emptyThreadUi();
        var tid = threadId || ui.activeThreadId;
        var raw = memberId != null && memberId !== ''
            ? memberId
            : (ui.addMemberId || '').trim();
        if (!tid) {
            if (typeof host.toast === 'function') {
                host.toast('Open a thread first', 'warning', 2500);
            }
            return;
        }
        var idStr = String(raw).trim();
        if (!idStr || !/^\d+$/.test(idStr)) {
            ui.error = 'Enter a numeric note ID (pnid)';
            host.threadUi = __assign(__assign({}, ui), { error: ui.error });
            if (typeof host.toast === 'function') {
                host.toast(ui.error, 'warning', 3000);
            }
            return;
        }
        if (!threadsApi) {
            if (typeof host.toast === 'function') {
                host.toast('Threads API not available', 'warning', 3000);
            }
            return;
        }
        var pid = personIdOf(host);
        var pathHint = pathHintForMember(host, idStr);
        var active = (0, threadStore_1.getThread)(pid, tid, storage);
        var role = (0, threadStore_1.defaultMemberRoleForKind)(active === null || active === void 0 ? void 0 : active.kind);
        var status = (0, threadStore_1.defaultMemberStatusForKind)(active === null || active === void 0 ? void 0 : active.kind);
        threadsApi
            .addMember(tid, {
            memberId: idStr,
            linkedBy: pid,
            pathHint: pathHint,
            role: role,
            status: status,
        })
            .then(function (member) {
            var withHint = member && isGenericMemberLabel(member.pathHint, member.memberId) && pathHint
                ? __assign(__assign({}, member), { pathHint: pathHint }) : member;
            var merged = __assign(__assign({}, withHint), { role: (withHint === null || withHint === void 0 ? void 0 : withHint.role) || role, status: (withHint === null || withHint === void 0 ? void 0 : withHint.status) || status });
            (0, threadStore_1.upsertLocalMember)(pid, merged, storage);
            invalidateThreadListCache();
            host.refreshThreadPills();
            host.openThread(tid, { skipHistory: true });
            if (typeof host.toast === 'function') {
                host.toast("Added note #".concat(idStr, " to thread"), 'success', 3000);
            }
        })
            .catch(function (e) {
            var msg = (e === null || e === void 0 ? void 0 : e.message) || 'Could not add member';
            if (host.threadUi) {
                host.threadUi.error = msg;
            }
            if (typeof host.toast === 'function') {
                host.toast(msg, 'warning', 4000);
            }
        });
    };
    host.closeThreadPanel = function () {
        host.threadUi = __assign(__assign({}, emptyThreadUi()), { showPanel: false });
        if (typeof window !== 'undefined') {
            var loc = window.location.pathname || '';
            if ((0, threadStore_1.isThreadsListUrl)(loc) || (0, threadStore_1.threadIdFromViewUrl)(loc)) {
                syncThreadLocation('/', { replace: true });
            }
        }
    };
    host.showThreadList = function () {
        var _a;
        invalidateThreadListCache();
        host.threadUi = __assign(__assign({}, emptyThreadUi()), { showPanel: true, mode: 'list', createAccountId: ((_a = host.person) === null || _a === void 0 ? void 0 : _a.CurrentAccount) || '' });
        syncThreadLocation((0, threadStore_1.threadsListUrl)());
        scrollThreadPanelIntoView();
        host.refreshThreadsFromServer({ quiet: true });
    };
    host.startCreateThread = function (row) {
        var _a;
        var accountHint = (row && (row.pnAccount || row.pnAssetAccount)) || ((_a = host.person) === null || _a === void 0 ? void 0 : _a.CurrentAccount) || '';
        host.threadUi = __assign(__assign({}, emptyThreadUi()), { showPanel: true, mode: 'create', pendingRow: row || null, createKind: 'invoice', createTitle: '', createAccountId: accountHint ? String(accountHint) : '' });
        syncThreadLocation((0, threadStore_1.threadsListUrl)());
        scrollThreadPanelIntoView();
    };
    host.createThreadFromForm = function () {
        var ui = host.threadUi || emptyThreadUi();
        var title = (ui.createTitle || '').trim();
        if (!title) {
            ui.error = 'Title is required';
            host.threadUi = ui;
            return;
        }
        var pid = personIdOf(host);
        var pending = ui.pendingRow;
        var input = {
            kind: (ui.createKind || 'custom'),
            title: title,
            accountId: (ui.createAccountId || '').trim() || null,
            createdBy: pid,
        };
        var afterLocal = function (thread) {
            if (pending && pending.ID != null) {
                try {
                    (0, threadStore_1.addMember)(pid, thread.id, {
                        memberId: pending.ID,
                        linkedBy: pid,
                        pathHint: pathHintForMember(host, String(pending.ID)),
                    }, storage);
                }
                catch (_a) {
                    /* ignore */
                }
            }
            invalidateThreadListCache();
            host.refreshThreadPills();
            host.openThread(thread.id);
            if (typeof host.toast === 'function') {
                host.toast("Thread created: ".concat((0, threadStore_1.threadPillLabel)(thread)), 'success', 3500);
            }
        };
        // No API (unit tests / cache-only): local create for menus still works
        if (!threadsApi) {
            try {
                afterLocal((0, threadStore_1.createThread)(pid, input, storage));
            }
            catch (e) {
                ui.error = (e === null || e === void 0 ? void 0 : e.message) || 'Could not create thread';
                host.threadUi = ui;
            }
            return;
        }
        threadsApi
            .createThread(input)
            .then(function (remote) {
            var thread = (0, threadStore_1.upsertLocalThread)(pid, remote, storage);
            if (pending && pending.ID != null) {
                var pathHint = pathHintForMember(host, String(pending.ID));
                var role_1 = (0, threadStore_1.defaultMemberRoleForKind)(thread.kind || input.kind);
                var status_1 = (0, threadStore_1.defaultMemberStatusForKind)(thread.kind || input.kind);
                return threadsApi
                    .addMember(thread.id, {
                    memberId: pending.ID,
                    linkedBy: pid,
                    pathHint: pathHint,
                    role: role_1,
                    status: status_1,
                })
                    .then(function (member) {
                    (0, threadStore_1.upsertLocalMember)(pid, __assign(__assign({}, member), { role: (member === null || member === void 0 ? void 0 : member.role) || role_1, status: (member === null || member === void 0 ? void 0 : member.status) || status_1 }), storage);
                    invalidateThreadListCache();
                    host.refreshThreadPills();
                    host.openThread(thread.id);
                    if (typeof host.toast === 'function') {
                        host.toast("Thread created: ".concat((0, threadStore_1.threadPillLabel)(thread)), 'success', 3500);
                    }
                })
                    .catch(function () {
                    invalidateThreadListCache();
                    host.refreshThreadPills();
                    host.openThread(thread.id);
                });
            }
            invalidateThreadListCache();
            host.refreshThreadPills();
            host.openThread(thread.id);
            if (typeof host.toast === 'function') {
                host.toast("Thread created: ".concat((0, threadStore_1.threadPillLabel)(thread)), 'success', 3500);
            }
        })
            .catch(function (e) {
            ui.error = (e === null || e === void 0 ? void 0 : e.message) || 'Could not create thread';
            host.threadUi = ui;
            if (typeof host.toast === 'function') {
                host.toast(ui.error, 'warning', 4000);
            }
        });
    };
    host.addRowToThread = function (row, threadId) {
        if (!row || row.ID == null || !threadId)
            return;
        var pid = personIdOf(host);
        // Caption from the source row itself (not only if it is already in recent/report lists)
        var pathHint = pathHintForRow(host, row);
        var threadMeta = (0, threadStore_1.getThread)(pid, threadId, storage);
        var role = (0, threadStore_1.defaultMemberRoleForKind)(threadMeta === null || threadMeta === void 0 ? void 0 : threadMeta.kind);
        var status = (0, threadStore_1.defaultMemberStatusForKind)(threadMeta === null || threadMeta === void 0 ? void 0 : threadMeta.kind);
        var finishOk = function (member) {
            var _a, _b, _c, _d;
            invalidateThreadListCache();
            host.refreshThreadPills();
            var thread = (0, threadStore_1.getThread)(pid, threadId, storage);
            if (thread) {
                host.recordThreadView(thread);
            }
            // Cache-only repaint (no GET /threads/:id) — avoids a second members fetch
            // that used to land just before a same-page <a href> navigation race.
            if (((_a = host.threadUi) === null || _a === void 0 ? void 0 : _a.showPanel) && host.threadUi.activeThreadId === threadId) {
                openThreadFromCache(threadId, { skipHistory: true });
            }
            var at = Date.now();
            var mid = (member === null || member === void 0 ? void 0 : member.memberId) != null ? member.memberId : row.ID;
            (0, threadMemberDigest_1.touchThreadMemberPatchAt)(pid, threadId, mid, at, storage);
            publishThreadMemberPatches([
                (0, threadMemberDigest_1.patchFromMemberChange)(threadId, mid, {
                    status: (_b = member === null || member === void 0 ? void 0 : member.status) !== null && _b !== void 0 ? _b : status,
                    role: (_c = member === null || member === void 0 ? void 0 : member.role) !== null && _c !== void 0 ? _c : role,
                    pathHint: (_d = member === null || member === void 0 ? void 0 : member.pathHint) !== null && _d !== void 0 ? _d : pathHint,
                }, at),
            ]);
            if (typeof host.toast === 'function') {
                var label = thread ? (0, threadStore_1.threadPillLabel)(thread) : threadId;
                host.toast("Added to thread: ".concat(label), 'success', 3000);
            }
        };
        if (!threadsApi) {
            try {
                var m = (0, threadStore_1.addMember)(pid, threadId, { memberId: row.ID, linkedBy: pid, pathHint: pathHint, role: role, status: status }, storage);
                finishOk(m);
            }
            catch (e) {
                if (typeof host.toast === 'function') {
                    host.toast((e === null || e === void 0 ? void 0 : e.message) || 'Could not add to thread', 'warning', 4000);
                }
            }
            return;
        }
        threadsApi
            .addMember(threadId, {
            memberId: row.ID,
            linkedBy: pid,
            pathHint: pathHint,
            role: role,
            status: status,
        })
            .then(function (member) {
            // Keep a good local pathHint even if API echo omits it
            var withHint = member && isGenericMemberLabel(member.pathHint, member.memberId) && pathHint
                ? __assign(__assign({}, member), { pathHint: pathHint }) : member;
            var merged = __assign(__assign({}, withHint), { role: (withHint === null || withHint === void 0 ? void 0 : withHint.role) || role, status: (withHint === null || withHint === void 0 ? void 0 : withHint.status) || status });
            (0, threadStore_1.upsertLocalMember)(pid, merged, storage);
            finishOk(merged);
        })
            .catch(function (e) {
            if (typeof host.toast === 'function') {
                host.toast((e === null || e === void 0 ? void 0 : e.message) || 'Could not add to thread', 'warning', 4000);
            }
        });
    };
    function finishMemberPatch(threadId, member, opts) {
        var _a;
        var pid = personIdOf(host);
        (0, threadStore_1.upsertLocalMember)(pid, member, storage);
        invalidateThreadListCache();
        host.refreshThreadPills();
        if (opts === null || opts === void 0 ? void 0 : opts.row)
            host.closeThreadPillMenu(opts.row);
        if (((_a = host.threadUi) === null || _a === void 0 ? void 0 : _a.showPanel) && host.threadUi.activeThreadId === threadId) {
            openThreadFromCache(threadId, { skipHistory: true });
        }
        if ((opts === null || opts === void 0 ? void 0 : opts.publish) !== false) {
            var at = (opts === null || opts === void 0 ? void 0 : opts.at) || Date.now();
            (0, threadMemberDigest_1.touchThreadMemberPatchAt)(pid, threadId, member.memberId, at, storage);
            publishThreadMemberPatches([
                (0, threadMemberDigest_1.patchFromMemberChange)(threadId, member.memberId, {
                    status: member.status,
                    role: member.role,
                    pathHint: member.pathHint,
                }, at),
            ]);
        }
    }
    /**
     * Set per-member progress status (`thread_member.status`).
     * Values: todo | planning | started | doing | deploying | testing |
     * pct* | done | bugfix | feedback | hidden | …
     */
    host.setThreadMemberStatus = function (threadId, memberId, status, opts) {
        if (!threadId || memberId == null || status == null)
            return;
        var pid = personIdOf(host);
        var members = (0, threadStore_1.listMembers)(pid, threadId, storage);
        var mid = String(memberId);
        var current = members.find(function (m) { return m.memberId === mid; });
        var nextStatus = String(status).trim();
        if (!threadsApi) {
            try {
                var updated = (0, threadStore_1.updateMember)(pid, threadId, memberId, { status: nextStatus }, storage);
                if (updated)
                    finishMemberPatch(threadId, updated, opts);
            }
            catch (e) {
                if (typeof host.toast === 'function') {
                    host.toast((e === null || e === void 0 ? void 0 : e.message) || 'Could not update status', 'warning', 3000);
                }
            }
            return;
        }
        threadsApi
            .updateMember(threadId, memberId, { status: nextStatus })
            .then(function (remote) {
            finishMemberPatch(threadId, __assign(__assign({}, remote), { pathHint: remote.pathHint || (current === null || current === void 0 ? void 0 : current.pathHint), role: remote.role || (current === null || current === void 0 ? void 0 : current.role), status: remote.status != null ? remote.status : nextStatus }), opts);
        })
            .catch(function (e) {
            // Optimistic local + stream so other tabs still move; warn that server failed
            try {
                var updated = (0, threadStore_1.updateMember)(pid, threadId, memberId, { status: nextStatus }, storage);
                if (updated) {
                    finishMemberPatch(threadId, updated, opts);
                    if (typeof host.toast === 'function') {
                        host.toast("Status saved locally only \u2014 server: ".concat((e === null || e === void 0 ? void 0 : e.message) || 'update failed'), 'warning', 5000);
                    }
                    return;
                }
            }
            catch (_a) {
                /* fall through */
            }
            if (typeof host.toast === 'function') {
                host.toast((e === null || e === void 0 ? void 0 : e.message) || 'Could not update status', 'warning', 4000);
            }
        });
    };
    /**
     * Set membership invoice/link role (`thread_member.role`).
     * Values: line_item | evidence | related | primary.
     * Progress values are routed to status for back-compat with old templates.
     */
    host.setThreadMemberRole = function (threadId, memberId, role, opts) {
        if (!threadId || memberId == null || role == null || role === '')
            return;
        var next = String(role).trim();
        // Progress options still call this from some templates — send to status column
        if ((0, threadStore_1.isMemberProgressStatus)(next)) {
            host.setThreadMemberStatus(threadId, memberId, next, opts);
            return;
        }
        var pid = personIdOf(host);
        var members = (0, threadStore_1.listMembers)(pid, threadId, storage);
        var mid = String(memberId);
        var current = members.find(function (m) { return m.memberId === mid; });
        if (!threadsApi) {
            try {
                var updated = (0, threadStore_1.updateMember)(pid, threadId, memberId, { role: next }, storage);
                if (updated)
                    finishMemberPatch(threadId, updated, opts);
            }
            catch (e) {
                if (typeof host.toast === 'function') {
                    host.toast((e === null || e === void 0 ? void 0 : e.message) || 'Could not update role', 'warning', 3000);
                }
            }
            return;
        }
        threadsApi
            .updateMember(threadId, memberId, { role: next })
            .then(function (remote) {
            finishMemberPatch(threadId, __assign(__assign({}, remote), { pathHint: remote.pathHint || (current === null || current === void 0 ? void 0 : current.pathHint), role: remote.role || next, status: remote.status || (current === null || current === void 0 ? void 0 : current.status) }), opts);
        })
            .catch(function (e) {
            try {
                var updated = (0, threadStore_1.updateMember)(pid, threadId, memberId, { role: next }, storage);
                if (updated) {
                    finishMemberPatch(threadId, updated, opts);
                    if (typeof host.toast === 'function') {
                        host.toast("Role saved locally only \u2014 server: ".concat((e === null || e === void 0 ? void 0 : e.message) || 'update failed'), 'warning', 5000);
                    }
                    return;
                }
            }
            catch (_a) {
                /* fall through */
            }
            if (typeof host.toast === 'function') {
                host.toast((e === null || e === void 0 ? void 0 : e.message) || 'Could not update role', 'warning', 4000);
            }
        });
    };
    /**
     * Cycle coarse progress status: todo → planning → started → … → done → todo.
     */
    host.cycleThreadMemberCheck = function (threadId, memberId, opts) {
        if (!threadId || memberId == null)
            return;
        var pid = personIdOf(host);
        var members = (0, threadStore_1.listMembers)(pid, threadId, storage);
        var mid = String(memberId);
        var current = members.find(function (m) { return m.memberId === mid; });
        var nextStatus = (0, threadStore_1.nextMemberCheckRole)((0, threadStore_1.memberEffectiveStatus)(current || {}));
        host.setThreadMemberStatus(threadId, memberId, nextStatus, opts);
    };
    host.removeRowFromThread = function (threadId, memberId) {
        var pid = personIdOf(host);
        var finishLocal = function (opts) {
            var _a;
            (0, threadStore_1.removeMember)(pid, threadId, memberId, storage);
            invalidateThreadListCache();
            host.refreshThreadPills();
            if (((_a = host.threadUi) === null || _a === void 0 ? void 0 : _a.showPanel) && host.threadUi.activeThreadId === threadId) {
                openThreadFromCache(threadId, { skipHistory: true });
            }
            if ((opts === null || opts === void 0 ? void 0 : opts.publish) !== false) {
                var at = Date.now();
                (0, threadMemberDigest_1.touchThreadMemberPatchAt)(pid, threadId, memberId, at, storage);
                publishThreadMemberPatches([
                    (0, threadMemberDigest_1.patchFromMemberChange)(threadId, memberId, { removed: true }, at),
                ]);
            }
            if (typeof host.toast === 'function') {
                host.toast('Removed from thread', 'info', 2500);
            }
        };
        if (!threadsApi) {
            if (!(0, threadStore_1.removeMember)(pid, threadId, memberId, storage)) {
                if (typeof host.toast === 'function') {
                    host.toast('Member not on thread', 'info', 2500);
                }
                return;
            }
            finishLocal();
            return;
        }
        threadsApi
            .removeMember(threadId, memberId)
            .then(function () { return finishLocal(); })
            .catch(function (e) {
            if (/not found/i.test((e === null || e === void 0 ? void 0 : e.message) || '')) {
                finishLocal();
                return;
            }
            if (typeof host.toast === 'function') {
                host.toast((e === null || e === void 0 ? void 0 : e.message) || 'Could not remove member', 'warning', 4000);
            }
        });
    };
    host.openThreadMember = function (memberId) {
        if (memberId == null)
            return;
        if (typeof host.personSearch === 'function') {
            host.personSearch({ pnid: memberId });
        }
    };
    host.renameActiveThread = function (title) {
        var ui = host.threadUi;
        if (!(ui === null || ui === void 0 ? void 0 : ui.activeThreadId))
            return;
        var pid = personIdOf(host);
        var tid = ui.activeThreadId;
        if (!threadsApi) {
            try {
                var next = (0, threadStore_1.updateThread)(pid, tid, { title: title }, storage);
                if (next)
                    host.openThread(next.id);
            }
            catch (e) {
                if (typeof host.toast === 'function') {
                    host.toast((e === null || e === void 0 ? void 0 : e.message) || 'Rename failed', 'warning', 3000);
                }
            }
            return;
        }
        threadsApi
            .updateThread(tid, { title: title })
            .then(function (remote) {
            (0, threadStore_1.upsertLocalThread)(pid, remote, storage);
            invalidateThreadListCache();
            host.openThread(tid);
        })
            .catch(function (e) {
            if (typeof host.toast === 'function') {
                host.toast((e === null || e === void 0 ? void 0 : e.message) || 'Rename failed', 'warning', 3000);
            }
        });
    };
    host.tryOpenThreadFromViewHistory = function (entry) {
        if (!(entry === null || entry === void 0 ? void 0 : entry.data))
            return false;
        var tid = (entry.data.threadId && String(entry.data.threadId)) ||
            (0, threadStore_1.threadIdFromViewUrl)(entry.data.url);
        if (!tid)
            return false;
        host.openThread(tid);
        return true;
    };
    host.applyThreadsPath = applyThreadsPath;
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window.addEventListener('popstate', function () {
            var _a;
            try {
                var path = window.location.pathname || '';
                if ((0, threadStore_1.isThreadsListUrl)(path) || (0, threadStore_1.threadIdFromViewUrl)(path)) {
                    applyThreadsPath(path);
                }
                else if ((_a = host.threadUi) === null || _a === void 0 ? void 0 : _a.showPanel) {
                    host.threadUi = __assign(__assign({}, emptyThreadUi()), { showPanel: false });
                }
            }
            catch (_b) {
                /* ignore */
            }
        });
    }
    var configuredPath = host.viewMode &&
        (host.viewMode.initialThreadsPath ||
            host.viewMode.pageMode === 'threads');
    var initialPath = configuredPath
        ? String(host.viewMode.initialThreadsPath ||
            (typeof window !== 'undefined' ? window.location.pathname : '/threads'))
        : '';
    if (initialPath && ((0, threadStore_1.isThreadsListUrl)(initialPath) || (0, threadStore_1.threadIdFromViewUrl)(initialPath))) {
        if (deps.$timeout) {
            deps.$timeout(function () { return applyThreadsPath(initialPath); }, 200);
        }
        else {
            applyThreadsPath(initialPath);
        }
    }
    // Quiet pull when person already known (panel open / login also pull)
    if (threadsApi && personIdOf(host) != null) {
        if (deps.$timeout) {
            deps.$timeout(function () {
                var _a;
                try {
                    host.refreshThreadsFromServer({ quiet: true });
                    if (((0, threadStore_1.isThreadsListUrl)(initialPath) || (0, threadStore_1.threadIdFromViewUrl)(initialPath)) &&
                        !((_a = host.threadUi) === null || _a === void 0 ? void 0 : _a.showPanel)) {
                        applyThreadsPath(initialPath);
                    }
                }
                catch (_b) {
                    /* ignore */
                }
            }, 800);
        }
        else {
            try {
                host.refreshThreadsFromServer({ quiet: true });
            }
            catch (_a) {
                /* ignore */
            }
        }
    }
}
exports.registerThreadScopeActions = registerThreadScopeActions;
